import { ProgressCircle, TextArea } from '@react-spectrum/s2';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { InlineEmailEditor } from '@/components/InlineEmailEditor/InlineEmailEditor';
import { getBlobAccessUrl } from '@/hooks/useBlobAccessUrl';
import { useOptimisticState } from '@/hooks/useOptimisticState';
import { logger } from '@/logger';
import { useBusinessProfileApi } from '@/spaces/brand/useBusinessProfileApi';

import type { EmailContent } from '../../../../bridge/types/artifact.ts';

import type { ArtifactComponentProps } from './types';

type EmailPersonalizationProps = ArtifactComponentProps<EmailContent>;

type EmailInstanceDoc = {
	id: string;
	user_id: string;
	template_id: string;
	html_link: string;
};

function isValidBlobUrl(url: string): boolean {
	try {
		return new URL(url).hostname.endsWith('.blob.core.windows.net');
	} catch {
		return false;
	}
}

export const EmailPersonalization = memo(function EmailPersonalization({
	content,
	isReadOnly = false,
	onContentChange,
}: EmailPersonalizationProps) {
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const { profile } = useBusinessProfileApi(userId);

	const brandColors = (profile?.brand_identity?.colors?.value ?? []).map((c) =>
		c.startsWith('#') ? c : `#${c}`,
	);
	const imageUrlEntries = profile?.brand_identity?.image_urls ?? [];
	const posts = profile?.brand_identity?.latest_posts?.value;
	const brandImages =
		imageUrlEntries.length > 0
			? imageUrlEntries.map((img) => ({ display_url: img.url }))
			: (posts ?? []).map((p) => ({ display_url: p.display_url }));

	const [localSubject, setLocalSubject] = useOptimisticState(content.subject);
	const committedBody = content.body ?? '';
	const [localBody, setLocalBody] = useOptimisticState(committedBody);

	const handleSubjectCommit = useCallback(
		(value: string) => {
			const trimmed = value.trim();
			if (trimmed && trimmed !== content.subject) {
				onContentChange?.({ subject: trimmed });
			}
		},
		[onContentChange, content.subject],
	);
	const handleBodyCommit = useCallback(
		(value: string) => {
			if (value !== committedBody) {
				onContentChange?.({ body: value });
			}
		},
		[committedBody, onContentChange],
	);

	const instance = content.instance;
	const hasInstance = instance != null;

	const [localHtml, setLocalHtml] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);

	const latestHtmlRef = useRef<string>('');
	const isDirtyRef = useRef(false);
	const pinnedHtmlLinkRef = useRef<string | null>(null);
	const pinnedCosmosIdRef = useRef<string | null>(null);
	const initialScanPendingRef = useRef(false);

	const saveToBlob = useCallback(async () => {
		if (
			!isDirtyRef.current ||
			!pinnedHtmlLinkRef.current ||
			!pinnedCosmosIdRef.current
		)
			return;
		if (!isValidBlobUrl(pinnedHtmlLinkRef.current)) {
			logger.warn('EmailPersonalization: invalid blob URL, skipping save');
			return;
		}

		await bridgewayApi.put(
			`/api/user/${userId}/email/instance/${pinnedCosmosIdRef.current}/html`,
			{ html: latestHtmlRef.current },
		);
		isDirtyRef.current = false;
		setSaveError(null);
	}, [bridgewayApi, userId]);

	useEffect(() => {
		return () => {
			void saveToBlob();
		};
	}, [saveToBlob]);

	const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const handleHtmlChange = useCallback(
		(html: string) => {
			latestHtmlRef.current = html;

			if (initialScanPendingRef.current) {
				initialScanPendingRef.current = false;
				return;
			}

			isDirtyRef.current = true;
			setSaveError(null);

			if (autoSaveTimerRef.current) {
				clearTimeout(autoSaveTimerRef.current);
			}
			autoSaveTimerRef.current = setTimeout(() => {
				saveToBlob().catch((err) => {
					logger.warn('EmailPersonalization: auto-save failed', { error: err });
					setSaveError(err instanceof Error ? err.message : 'Failed to save');
				});
			}, 5000);
		},
		[saveToBlob],
	);

	useEffect(() => {
		return () => {
			if (autoSaveTimerRef.current) {
				clearTimeout(autoSaveTimerRef.current);
			}
		};
	}, []);

	useEffect(() => {
		const handlePageHide = () => {
			void saveToBlob();
		};
		window.addEventListener('pagehide', handlePageHide);
		return () => window.removeEventListener('pagehide', handlePageHide);
	}, [saveToBlob]);

	const loadInstanceHtml = useCallback(
		(cosmosId: string, cancelledRef: { current: boolean }) => {
			setIsLoading(true);
			setError(null);
			setLocalHtml(null);

			bridgewayApi
				.get<EmailInstanceDoc>(`/api/user/${userId}/email/instance/${cosmosId}`)
				.then((instance) => {
					if (cancelledRef.current) return;
					if (!instance.html_link) {
						throw new Error('Email instance has no HTML link');
					}
					pinnedHtmlLinkRef.current = instance.html_link;
					pinnedCosmosIdRef.current = cosmosId;
					return getBlobAccessUrl(instance.html_link, userId);
				})
				.then((accessUrl) => {
					if (!accessUrl || cancelledRef.current) return;
					return fetch(accessUrl);
				})
				.then((res) => {
					if (!res || cancelledRef.current) return;
					if (!res.ok)
						throw new Error(`Failed to load email HTML: ${res.status}`);
					return res.text();
				})
				.then((html) => {
					if (!html || cancelledRef.current) return;
					setLocalHtml(html);
					latestHtmlRef.current = html;
					isDirtyRef.current = false;
					initialScanPendingRef.current = true;
					setIsLoading(false);
				})
				.catch((err) => {
					if (cancelledRef.current) return;
					setError(err instanceof Error ? err.message : 'Failed to load email');
					setIsLoading(false);
				});
		},
		[bridgewayApi, userId],
	);

	useEffect(() => {
		if (!instance || !userId) {
			setLocalHtml(null);
			pinnedHtmlLinkRef.current = null;
			pinnedCosmosIdRef.current = null;
			return;
		}

		const cancelledRef = { current: false };
		loadInstanceHtml(instance.cosmos_id, cancelledRef);

		return () => {
			cancelledRef.current = true;
		};
	}, [instance, userId, loadInstanceHtml]);

	const renderSubject = () => {
		if (isReadOnly) {
			return (
				<span className="flex-1 text-base font-semibold text-gray-900">
					{localSubject || '(No subject)'}
				</span>
			);
		}
		return (
			<input
				type="text"
				aria-label="Subject"
				value={localSubject}
				onChange={(event) => setLocalSubject(event.currentTarget.value)}
				onBlur={() => handleSubjectCommit(localSubject)}
				className="flex-1 border-none bg-transparent text-base font-semibold
					text-gray-900 outline-none placeholder:font-normal
					placeholder:text-gray-500"
				placeholder="Subject"
			/>
		);
	};

	const renderBody = () => {
		if (!hasInstance) {
			if (committedBody.trim().length > 0) {
				if (isReadOnly) {
					return (
						<p
							style={{
								fontSize: '0.875rem',
								color: 'var(--text-secondary)',
								lineHeight: 1.6,
								whiteSpace: 'pre-wrap',
							}}
						>
							{committedBody}
						</p>
					);
				}
				return (
					<TextArea
						aria-label="Email body"
						value={localBody}
						onChange={(value) => setLocalBody(value)}
						onBlur={() => handleBodyCommit(localBody)}
					/>
				);
			}
			return (
				<p
					style={{
						fontSize: '0.875rem',
						color: 'var(--text-muted)',
						fontStyle: 'italic',
						padding: '1rem 0',
					}}
				>
					No email template
				</p>
			);
		}

		if (isLoading) {
			return (
				<div className="flex items-center gap-3 py-4">
					<ProgressCircle isIndeterminate size="S" aria-label="Loading" />
					<span className="text-sm text-(--text-muted)">
						Loading email editor…
					</span>
				</div>
			);
		}

		if (error) {
			return (
				<div className="flex flex-col gap-2 py-4">
					<span className="text-sm text-red-600">{error}</span>
					<button
						type="button"
						onClick={() => {
							if (instance) {
								loadInstanceHtml(instance.cosmos_id, {
									current: false,
								});
							}
						}}
						className="w-fit text-sm text-blue-600 underline"
					>
						Retry
					</button>
				</div>
			);
		}

		if (!localHtml) return null;

		if (isReadOnly) {
			return (
				<iframe
					srcDoc={localHtml}
					sandbox="allow-same-origin"
					className="h-full min-h-0 w-full border-0"
					style={{ minHeight: 400 }}
					title="Email preview"
				/>
			);
		}

		return (
			<div
				className="flex min-h-0 min-w-0 flex-1 flex-col gap-3"
				style={{ minHeight: 400 }}
			>
				{saveError && (
					<div
						className="flex items-center gap-2 rounded border border-red-200
							bg-red-50 px-3 py-2"
					>
						<span className="text-sm text-red-600">{saveError}</span>
						<button
							type="button"
							onClick={() => {
								setSaveError(null);
								saveToBlob().catch((err) => {
									setSaveError(
										err instanceof Error ? err.message : 'Failed to save',
									);
								});
							}}
							className="text-sm text-blue-600 underline"
						>
							Retry
						</button>
					</div>
				)}
				<InlineEmailEditor
					html={localHtml}
					onHtmlChange={handleHtmlChange}
					brandColors={brandColors.length > 0 ? brandColors : null}
					brandImages={brandImages.length > 0 ? brandImages : null}
				/>
			</div>
		);
	};

	return (
		<div
			className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg
				border border-gray-200 bg-white"
			style={{ minHeight: hasInstance ? 400 : undefined }}
		>
			<div
				className="flex shrink-0 items-baseline gap-4 border-b border-gray-200
					px-5 py-3"
			>
				<span className="w-16 shrink-0 text-sm text-gray-500">Subject</span>
				{renderSubject()}
			</div>
			<div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
				{renderBody()}
			</div>
		</div>
	);
});
